import { Feature } from 'geojson';
import React, { useContext, useEffect, useMemo, useState } from "react";


import leafletImage from "leaflet-image";
import {
    useMap
} from 'react-leaflet';

import CloseIcon from '@mui/icons-material/Close';

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import "react-leaflet-fullscreen/styles.css";

import { LocationsContext } from '../context/locations';
import { MapDataContext } from '../context/map-data';

import { Button, IconButton } from '@mui/material';
import { getGeoJsonByBbox } from '../api/maps';
import { AccountContext } from '../context/account';
import { ProviderShapeItem, mapProvidersShape } from '../data/layers';
import { addCredits, createCanvasWithGraticuleBorder, createGradientBase64, drawGeoJsonOnCanvas, drawImageOnCanvas, drawImageOnCanvasOnly, drawScaleLegend } from '../helpers/canvas';
import { convertGeomToGeoJSON, delay } from '../helpers/core';
import useColourScale from '../hooks/useColourScale';
import useLoggedIn from '../hooks/useLoggedIn';
import useWindow, { DefaultWindow } from '../hooks/useWindow';
import styles from "../styles/components/selected-area-layer.module.scss";
import Loader from './loader';
import { LoginPanel } from './login-panel';

/**
 * Decodes a base64 string and saves it as a PNG file using the browser's file-saving mechanism.
 * @param base64Data - The base64-encoded string of the PNG file.
 * @param fileName - The name of the file to save (e.g., "image.png").
 */

const saveBase64AsPng = (base64Data: string, fileName: string, _window: Window | DefaultWindow) => {
  const base64 = base64Data.split(',').pop() || '';
  const binary = _window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'image/png' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export const PreviewButton = () => {
  const [showImagePreview, setShowImagePreview] = useState<boolean>(false);

  return <>
    <Button
      onClick={(e: React.SyntheticEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setShowImagePreview(true)
      }}
      variant="contained"
      className={styles.captureButton}
    >
      <PhotoCameraIcon />
    </Button>
    {showImagePreview && <PreviewOverlay onClose={() => {
      setShowImagePreview(false)
    }} />}
  </>
}

interface PreviewOverlayProps {
  onClose: () => void
}

export const PreviewOverlay = ({
  onClose,
}: PreviewOverlayProps) => {
  const [loading, setLoading] = useState(false)
  const [imageData, setImageData] = useState<string | null>(null)
 
  const map = useMap();
  const _window = useWindow()
  
  const { 
    setProjectPoints,
  } = useContext(AccountContext);
  

  const { 
    dataSourceModel,
    currentMapProvider,
    meshDataGeoJson, 
    setMeshDataGeoJson,
    currentLayer,
    showModelGrid,
    hasModelGrid,
    dataSources,
    showValidations,
  } = useContext(MapDataContext);

  const {
    visibleLayerLocationShapes,
  } = useContext(LocationsContext)

  const {getColour, lookupTable, useMin, useMax, showCustomScale: dynamicScaling, useMinLabel, useMaxLabel, valueFormat} = useColourScale();

  useEffect(() => {
    if (onClose) {
      const onPressESC = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }

      document.addEventListener("keydown", onPressESC, false);
      return () => {
        document.removeEventListener("keydown", onPressESC, false);
      }  
    }
  }, [onClose])

  const useProvider: ProviderShapeItem | null = useMemo(() => {
    if (currentMapProvider !== null && Object.prototype.hasOwnProperty.call(mapProvidersShape, currentMapProvider)) {
      return mapProvidersShape[currentMapProvider]
    }
    return null
  }, [currentMapProvider])

  const shapeFileGeoJSON = useMemo(() => {
    if (!visibleLayerLocationShapes?.length) return null;
    
    const features: Feature[] = [];
    visibleLayerLocationShapes.forEach(d => {
      if (d.hidden) return;
      if (d.data?.features) {
        features.push(...d.data.features);
      } else if (d.geom) {
        const f = convertGeomToGeoJSON(d.geom);
        f.properties = { ...f.properties, color: d.color || '#ffffff' };
        features.push(f);
      }
    });

    return { type: 'FeatureCollection', features };
  }, [visibleLayerLocationShapes]);

  const currentPointGeoJSON = useMemo(() => {
    if (!meshDataGeoJson) return null;
    return { type: 'FeatureCollection', features: meshDataGeoJson.features };
  }, [meshDataGeoJson]);

  useEffect(() => {
    if (dynamicScaling && lookupTable.length === 0) return;
    const generateImage = async () => {
      if (!map) return;
  
      setLoading(true);
      setProjectPoints([]);
      setMeshDataGeoJson(null);
  
      await delay(50);
      const processImage = async (_err: any, canvas: HTMLCanvasElement) => {
        if (!canvas || !map) return;

        const bounds = map.getBounds();
        const bbox: [number, number, number, number] = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ];
  
        if (dynamicScaling && dataSourceModel && !dynamicScaling) {
          const { model, provider, geography, version } = dataSourceModel;
          const areaJSON = await getGeoJsonByBbox({ coords: bbox, model, provider, geography, version });
  
          if (areaJSON?.response) {
            drawGeoJsonOnCanvas(canvas, areaJSON.response, {
              bbox,
              onFeatureDrawn: (properties: any) => {
                if (!currentLayer || !properties) {
                  return { fill: "", line: "",};
                } 
                const {valueKey} = currentLayer;
                const useKey = typeof valueKey === 'string' ? valueKey.split('-vector')[0] : valueKey
                let value = NaN 
                if (typeof useKey === "string" && Object.prototype.hasOwnProperty.call(properties, useKey)) {
                  value = Number(properties[useKey]);
                }
                
                if (Number.isNaN(value)) {
                  return { fill: "", line: "",};
                }
                const fillColor = getColour(value);
  
                return {
                  fill: fillColor,
                  line: (showModelGrid && hasModelGrid) ? '#000000' : fillColor,
                };
            
              },
            });
          }
        }
  
        if (shapeFileGeoJSON) {
          drawGeoJsonOnCanvas(canvas, shapeFileGeoJSON, { bbox });
        }
        if (currentPointGeoJSON) {
          drawGeoJsonOnCanvas(canvas, currentPointGeoJSON, { bbox, lineColor: '#FF7F01' });
        }
        
        const newCanvas = await createCanvasWithGraticuleBorder(canvas, bbox, canvas.toDataURL());
  
        if  (currentLayer) {
          const { colours, reverse, title, attribution = '' } = currentLayer;
        
          const useColours = !reverse ? Object.values(colours || {}) : Object.values(colours || {}).reverse()
    
          // @ts-ignore
          const gradient = createGradientBase64(useColours, 90)
    
          const zoom = map.getZoom();
          const southLat = bounds.getSouth();
    
          const legendCanvas = drawScaleLegend(zoom, newCanvas, southLat);
          
          await drawImageOnCanvas(legendCanvas, gradient, 30, canvas.height, title, useMin, useMax, reverse, valueFormat);
          const credits = [attribution];
          if (useProvider?.attribution) credits.push(useProvider.attribution);
          await addCredits(newCanvas, credits);
  
        }

        try {
          await drawImageOnCanvasOnly(newCanvas, './moa-logo-inline-full.png', 24, 24, 286, 42);
        } catch {}
  
        setImageData(newCanvas.toDataURL());
        setLoading(false);
      }

      map.eachLayer((layer: any) => {
      
        if (layer._url === undefined) {
          console.warn('Leaflet layer has undefined _url', layer);
        }
      
        if (layer._icon?.src === undefined) {
          console.warn('Leaflet icon has undefined src', layer);
        }
      
        if (layer._image?.src === undefined) {
          console.warn('Leaflet image overlay has undefined src', layer);
        }
      });
      
      leafletImage(map, processImage);
    }
    
    generateImage();
  }, [
    map,
    currentLayer,
    dataSourceModel,
    dynamicScaling,
    lookupTable,
    showModelGrid,
    hasModelGrid,
    shapeFileGeoJSON,
    currentPointGeoJSON,
    useProvider,
    useMin,
    useMax,
    getColour,
    showValidations,
  ]);

  const isLoggedIn = useLoggedIn();

  return (
    <>
      {loading ? 
        <Loader showCog center middle overlay title='Generating screenshot, please wait...' />
        :
        <>
          {imageData && 
            <div
              className={styles.imageOverlay}
            >
              <div className={styles.overlay}></div>
              <div className={styles.content}>
                <img src={imageData} alt="" />
                <LoginPanel top={100} message={'To download the screenshot, please **login** or **create a free account**.'} />
                {isLoggedIn && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!dataSourceModel || !dataSources) return;
                      const ds = dataSources[dataSourceModel.key];
                      saveBase64AsPng(
                        imageData,
                        `${ds?.name} - ${currentLayer?.shortName || ''}.png`,
                        _window
                      );
                    }}
                  >
                    Download image
                  </Button>
                )}
                <IconButton
                  style={{position: 'absolute', top: 16, right: 16, zIndex: 1000}}
                  onMouseDown={(e: React.SyntheticEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                    e.nativeEvent.stopImmediatePropagation();
                    setImageData(null)
                    onClose()
                  }}>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          }
        </>
      }
    </>
  );
};